/**
 * Client-side mirror of the dimensions backend bounds
 * (`services/quill-admin-backend/src/routes/dimensions.js`). Validating here
 * BEFORE submit gives the admin immediate feedback and blocks an obviously bad
 * save; the backend still re-validates and its 400 DIMENSION_BOUNDS message is
 * surfaced verbatim if the two ever disagree. Same arrangement as
 * `mirror-guardian/guardianValidation.ts`.
 *
 * These two numbers are not cosmetic. quill-engine reads them:
 *
 *   decay_half_life_days → weightsRepo → evidenceScoring.decayFactor,
 *     τ_d in `0.5 ^ (ageDays / τ)`. How fast a memory's evidence loses weight.
 *   importance           → weightsRepo → interviewQueueService,
 *     a multiplier in `rank_score = priority × (1−coverage) × importance × fit`.
 *     Which questions a member is asked next.
 *
 * Bounds (must match the route):
 *   - decay_half_life_days: whole number, 30..3650 (a month to ten years)
 *   - importance:           0.01..1.00, at most 2 decimal places
 *
 * WHY THE IMPORTANCE FLOOR IS 0.01 AND NOT 0: zero is not a very low weight, it
 * is a total silencing — the rank product collapses to 0 and the dimension's
 * questions sit below every other candidate forever, with nothing on any screen
 * saying so. Stopping a dimension being asked about is what archiving its
 * questions and is_internal_only are for. 0.01 is the smallest value the
 * NUMERIC(3,2) column can hold, so the floor forbids exactly the corrupting
 * value and nothing an admin could mean.
 *
 * WHY 2 DECIMAL PLACES: the column is NUMERIC(3,2). A finer value is not
 * refused by Postgres — it is silently rounded, so the admin would be told a
 * number they did not type had been saved.
 */

export const HALF_LIFE_MIN = 30;
export const HALF_LIFE_MAX = 3650;
export const IMPORTANCE_MIN = 0.01;
export const IMPORTANCE_MAX = 1;
export const IMPORTANCE_DECIMALS = 2;

export interface DimensionTuningDraft {
  decay_half_life_days: number;
  importance: number;
}

/** Field-keyed validation errors; empty object === valid. */
export type DimensionTuningErrors = Partial<Record<keyof DimensionTuningDraft, string>>;

export function validateDimensionTuning(d: DimensionTuningDraft): DimensionTuningErrors {
  const errors: DimensionTuningErrors = {};

  if (
    !Number.isInteger(d.decay_half_life_days) ||
    d.decay_half_life_days < HALF_LIFE_MIN ||
    d.decay_half_life_days > HALF_LIFE_MAX
  ) {
    errors.decay_half_life_days =
      `Decay half-life must be a whole number of days from ${HALF_LIFE_MIN} to ${HALF_LIFE_MAX}.`;
  }

  if (
    !Number.isFinite(d.importance) ||
    d.importance < IMPORTANCE_MIN ||
    d.importance > IMPORTANCE_MAX
  ) {
    errors.importance =
      `Importance must be between ${IMPORTANCE_MIN} and ${IMPORTANCE_MAX}. ` +
      'Zero is not a low weight — it removes the dimension from interview ranking entirely.';
  } else if (Number(d.importance.toFixed(IMPORTANCE_DECIMALS)) !== d.importance) {
    errors.importance =
      `Importance is stored to ${IMPORTANCE_DECIMALS} decimal places; a finer value would be ` +
      'rounded on write and saved as a number you did not choose.';
  }

  return errors;
}
